import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Retainer,
  RetainersListResponse,
} from '@stfors/shared-types';
import { api } from '../client';

export interface RetainerFilters {
  client_id?: number;
  project_id?: number;
}

export function useRetainers(filters: RetainerFilters = {}) {
  const params = new URLSearchParams();
  if (filters.client_id !== undefined) params.set('client_id', String(filters.client_id));
  if (filters.project_id !== undefined) params.set('project_id', String(filters.project_id));
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/retainers?${qs}` : '/api/retainers';
  return useQuery({
    queryKey: ['retainers', { client_id: filters.client_id ?? null, project_id: filters.project_id ?? null }],
    queryFn: ({ signal }) => api.get<RetainersListResponse>(url, signal),
  });
}

export interface CreateRetainerInput {
  client_id: number;
  project_id?: number;
  initial_amount: string;
  effective_from: number;
  notes?: string;
}

export function useCreateRetainer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRetainerInput) =>
      api.post<{ retainer: Retainer }>('/api/retainers', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['retainers'] });
    },
  });
}

export interface DrawRetainerInput {
  id: number;
  amount: string;
  notes?: string;
}

export function useDrawRetainer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: DrawRetainerInput) =>
      api.post<{ retainer: Retainer }>(`/api/retainers/${id}/draw`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['retainers'] });
    },
  });
}
